import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { toast } from "../../helper/common";
import ApplicationContext from "../../context/application/ApplicationContext";
import CloseIcon from "../../assets/img/closeIcon.svg";
import SkorDefaultLoader from "../../assets/img/skorDefaultLoader.gif";
import useTranslator from "../../hooks/useTranslator";
import AuthContext from "../../context/auth/AuthContext";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CheckIcon from "@mui/icons-material/Check";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { Tooltip as ReactTooltip } from "react-tooltip";

import {
  emailPrefix,
  getInitials,
  resolveParticipantName,
  getApplicationTitle,
} from "../../helper/applicationHelpers";
import SkorProtectBadge from "../SkorProtectBadge/SkorProtectBadge";

const resolveCreatorName = (created_by) => {
  if (!created_by || typeof created_by === "string") return "";
  const fname = created_by.fname || created_by.firstname || "";
  const lname = created_by.lname || created_by.lastname || "";
  return `${fname} ${lname}`.trim() || created_by.email || "";
};

const resolveCreatorEmail = (created_by) => {
  if (!created_by || typeof created_by === "string") return null;
  return created_by.email ?? null;
};

const resolveCreatorId = (created_by) => {
  if (!created_by) return null;
  if (typeof created_by === "string") return created_by;
  return created_by._id ?? null;
};

const formatPaymentTimeRemaining = (expiresAt, now) => {
  const remainingMs = Math.max(0, new Date(expiresAt).getTime() - now);
  const totalMinutes = Math.floor(remainingMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};
const getParticipantGuarantors = (participant) =>
  participant?.consent_status === "accepted" &&
  Array.isArray(participant?.guarantors)
    ? participant.guarantors
    : [];

/* ── role badge ── */
const RoleBadge = ({ role, _t }) => {
  switch (role) {
    case "deleted":
      return (
        <span className="app-member-badge app-member-badge-invited">
          {_t("deletedUser", "Deleted user")}
        </span>
      );
    case "owner":
      return (
        <span className="app-member-badge app-member-badge-owner">
          {_t("owner", "Owner")}
        </span>
      );
    case "invited":
      return (
        <span className="app-member-badge app-member-badge-invited">
          {_t("invited", "Invited")}
        </span>
      );
    case "accepted":
      return (
        <span className="app-member-badge app-member-badge-accepted">
          {_t("accepted", "Accepted")}
        </span>
      );
    case "pending":
      return (
        <span className="app-member-badge app-member-badge-invited">
          {_t("pending", "Pending")}
        </span>
      );
    case "rejected":
      return (
        <span className="app-member-badge app-member-badge-rejected">
          {_t("rejected", "Rejected")}
        </span>
      );
    default:
      return null;
  }
};

/* ── single application card ── */
const ApplicationCard = ({
  app,
  currentUserEmail,
  currentUserId,
  onAccept,
  onReject,
  onDelete,
  processingId,
  openMenuId,
  setOpenMenuId,
  onCloseModal,
  llShowOnly,
  pendingSkorProtectAction,
  now,
  onPaySkorProtect,
  paymentProcessingId,
  onViewPaymentReceipt,
}) => {
  const { _t } = useTranslator();
  const [expanded, setExpanded] = useState(false);
  const menuRef = useRef(null);
  const isProcessing = processingId === app._id;
  const menuOpen = openMenuId === app._id;
  const skorProtectActionStatus =
    pendingSkorProtectAction?.status ?? "waiting_for_payment";
  const isSkorProtectPaymentPending =
    skorProtectActionStatus === "waiting_for_payment";
  const isSkorProtectPaymentReceived =
    skorProtectActionStatus === "waiting_for_rental_contract";
  const isSkorProtectActive = skorProtectActionStatus === "active";
  const showSkorProtectDocuments =
    isSkorProtectPaymentReceived || isSkorProtectActive;
  const activePendingSkorProtectAction =
    pendingSkorProtectAction &&
    (showSkorProtectDocuments ||
      (isSkorProtectPaymentPending &&
        new Date(pendingSkorProtectAction.expires_at).getTime() > now))
      ? pendingSkorProtectAction
      : null;
  const isPaymentProcessing =
    paymentProcessingId === activePendingSkorProtectAction?.skor_protect_id;

  const participants = app.participants ?? [];
  const creatorEmail = resolveCreatorEmail(app.created_by);
  const creatorId = resolveCreatorId(app.created_by);
  const iAmOwner =
    (creatorId && creatorId === currentUserId) ||
    (creatorEmail && creatorEmail.toLowerCase() === currentUserEmail);

  const isPendingInvitation =
    app.isPendingInvitation ??
    (!iAmOwner &&
      participants.some(
        (p) =>
          p.email?.toLowerCase() === currentUserEmail &&
          p.consent_status === "pending"
      ));

  const pendingCount = participants.filter(
    (p) =>
      p.consent_status === "pending" &&
      p.email?.toLowerCase() !== creatorEmail?.toLowerCase()
  ).length;

  const shareRequests = app.shareRequests ?? [];
  const sharedProfiles = (app.sharedProfiles ?? []).filter(
    (sharedProfile) =>
      sharedProfile?.id?._id || sharedProfile?.shared_by_deleted
  );
  const totalShared = shareRequests.length + sharedProfiles.length;

  /* status badge under the card header */
  const getStatusBadge = () => {
    if (isPendingInvitation) {
      const name = resolveCreatorName(app.created_by);
      return (
        <span className="application-status-badge application-badge-invitation">
          {_t("invitationFrom")} {name}
        </span>
      );
    }

    if (pendingCount > 0) {
      return (
        <span className="application-status-badge application-badge-pending">
          {_t("pendingConsent")} &bull; {pendingCount}{" "}
          {pendingCount === 1 ? _t("flatmate") : _t("flatmates")}
        </span>
      );
    }

    if (totalShared > 0) {
      return (
        <span className="application-status-badge application-badge-shared">
          {_t("sharedWithApplication")} {totalShared}{" "}
          {totalShared === 1 ? _t("landlordApp") : _t("landlordsApp")}
        </span>
      );
    }

    return null;
  };

  /* determine the role of a single participant */
  const getRole = (participant) => {
    if (participant.is_deleted || participant.deleted) return "deleted";
    const isOwnerParticipant =
      creatorEmail &&
      participant.email?.toLowerCase() === creatorEmail.toLowerCase();
    if (isOwnerParticipant) return "owner";

    const isMe = participant.email?.toLowerCase() === currentUserEmail;
    if (isMe && participant.consent_status === "pending") return "invited";

    return participant.consent_status ?? "pending";
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  return (
    <div
      onClick={() => {
        setExpanded((v) => !v);
        setOpenMenuId(null);
      }}
      className={`application-card cursor-pointer${isPendingInvitation ? " application-card-invitation" : ""}`}
    >
      {/* ── header row ── */}
      <div className="application-card-row">
        <div className="application-card-icon-wrap">
          <div className="d-flex align-items-center custom-gap-3 position-relative">
            <div className="earner-thumbnail-wrapper earner-thumbnail-wrapper-px d-flex align-items-center justify-content-center">
              <img
                src="/images/multiple_users_icon_unfilled.svg"
                alt="User Icon"
              />
            </div>
            {participants?.length > 0 && (
              <div className="member-count-wrapper d-flex align-items-center justify-content-center">
                {participants.length}
              </div>
            )}
          </div>
        </div>

        <div className="application-card-info">
          <div className="application-card-name-row">
            <div className="d-flex align-items-center flex-wrap gap-2">
              <p
                className="application-card-name"
                title={getApplicationTitle(app, _t)}
              >
                {getApplicationTitle(app, _t)}
              </p>
              {app.skor_protect && <SkorProtectBadge />}
            </div>
            {activePendingSkorProtectAction && isSkorProtectPaymentPending && (
              <div className="application-skor-protect-pending-chip">
                <AccessTimeRoundedIcon />
                <span>{_t("skorProtect")}</span>
                <span aria-hidden="true">·</span>
                <span>{_t("paymentPending")}</span>
                <span aria-hidden="true">·</span>
                <span>
                  {formatPaymentTimeRemaining(
                    activePendingSkorProtectAction.expires_at,
                    now
                  )}
                </span>
              </div>
            )}
            {activePendingSkorProtectAction && isSkorProtectPaymentReceived && (
              <>
                <div
                  className="application-skor-protect-paid-chip"
                  data-tooltip-id={`skor-protect-paid-${app._id}`}
                >
                  <ShieldOutlinedIcon />
                  <span>{_t("skorProtect")}</span>
                  <span aria-hidden="true">·</span>
                  <span>{_t("paid")}</span>
                  <InfoOutlinedIcon />
                </div>
                <ReactTooltip
                  id={`skor-protect-paid-${app._id}`}
                  place="bottom"
                  className="react-tooltip-styling application-skor-protect-paid-tooltip"
                  render={() => (
                    <p>
                      {_t(
                        "skorProtectPaidTooltip",
                        "Payment received. Skor Protect will become active as soon as the landlord uploads the rental agreement."
                      )}
                    </p>
                  )}
                />
              </>
            )}
            {activePendingSkorProtectAction && isSkorProtectActive && (
              <div className="application-skor-protect-active-chip">
                <ShieldOutlinedIcon />
                <span>{_t("skorProtect")}</span>
                <span aria-hidden="true">·</span>
                <span>{_t("active")}</span>
              </div>
            )}
            <div className="application-avatars">
              {participants.slice(0, 4).map((p, i) => {
                const name = resolveParticipantName(
                  p,
                  _t("deletedUser", "Deleted user")
                );
                const isPending = p.consent_status === "pending";
                const isAccepted = p.consent_status === "accepted";
                const guarantors = getParticipantGuarantors(p);
                return (
                  <div key={i} className="application-avatar-stack">
                    <div
                      className={`member-avatar-chip member-avatar-chip-gray position-relative${isAccepted ? " member-avatar-chip-tenant" : ""}${isPending ? " member-avatar-chip-pending" : ""}`}
                      title={name}
                      style={{ zIndex: i }}
                    >
                      {getInitials(name)}
                      {isPending && (
                        <span className="member-chip-warn">
                          <img src="/images/warning-dark-yellow.svg" alt="" />
                        </span>
                      )}
                    </div>
                    {guarantors.slice(0, 3).map((guarantor, guarantorIndex) => {
                      const guarantorName = resolveParticipantName(guarantor);
                      return (
                        <div
                          key={guarantor._id ?? guarantorIndex}
                          className="member-avatar-chip member-avatar-chip-guarantor"
                          title={`${guarantorName} · ${_t("guarantor", "Guarantor")}`}
                        >
                          {getInitials(guarantorName)}
                        </div>
                      );
                    })}
                    {guarantors.length > 3 && (
                      <div
                        className="member-avatar-chip member-avatar-chip-guarantor member-avatar-chip-overflow"
                        title={`${guarantors.length - 3} ${_t("more", "more")}`}
                      >
                        +{guarantors.length - 3}
                      </div>
                    )}
                  </div>
                );
              })}
              {participants.length > 4 && (
                <div className="member-avatar-chip member-avatar-chip-gray">
                  +{participants.length - 4}
                </div>
              )}
            </div>
          </div>
          {/* <p className="application-card-members">
            {participants.length} {_t("members")}
          </p> */}
        </div>

        <div className="application-card-controls">
          <button
            className="application-chevron-btn"
            // onClick={() => setExpanded((v) => !v)}
            aria-label="toggle"
          >
            {expanded ? (
              <KeyboardArrowUpIcon fontSize="small" />
            ) : (
              <KeyboardArrowDownIcon fontSize="small" />
            )}
          </button>

          {isPendingInvitation ? (
            <>
              <button
                className="application-reject-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onReject(app._id);
                }}
                disabled={isProcessing}
                aria-label="reject"
              >
                <CloseRoundedIcon fontSize="small" />
              </button>
              <button
                className="application-accept-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onAccept(app._id);
                }}
                disabled={isProcessing}
                aria-label="accept"
              >
                <CheckIcon fontSize="small" />
              </button>
            </>
          ) : !llShowOnly ? (
            <div className="position-relative" ref={menuRef}>
              <MoreVertIcon
                className="cursor-pointer"
                style={{ color: "#141B34" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenMenuId(menuOpen ? null : app._id);
                }}
              />
              {menuOpen && (
                <div className="editDeleteDropdown share-profile-three-dot-modal">
                  {showSkorProtectDocuments && (
                    <>
                      <p
                        className="custom-gap-2 application-paid-menu-item"
                        style={{ color: "#141B34" }}
                        onClick={(event) => {
                          event.stopPropagation();
                          setOpenMenuId(null);
                          onViewPaymentReceipt(activePendingSkorProtectAction);
                        }}
                      >
                        <DescriptionOutlinedIcon />
                        {_t("viewPaymentReceipt")}
                      </p>
                      <p
                        className="custom-gap-2 application-paid-menu-item"
                        style={{ color: "#141B34" }}
                        onClick={(event) => {
                          event.stopPropagation();
                          setOpenMenuId(null);
                          window.open(
                            "https://www.skortorent.com/inquilino/skor-protect/",
                            "_blank",
                            "noopener,noreferrer"
                          );
                        }}
                      >
                        <ShieldOutlinedIcon />
                        {_t("viewSkorProtectTermsAndConditions")}
                      </p>
                      <div
                        className="application-paid-menu-divider"
                        role="separator"
                      />
                    </>
                  )}
                  <p
                    className="custom-gap-2"
                    style={{ color: "#141B34" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenuId(null);
                      onCloseModal();
                      const url = new URL(window.location.href);
                      url.searchParams.set("shareprofilemodal", "true");
                      url.searchParams.set("applicationId", app._id);
                      window.history.replaceState(
                        window.history.state,
                        "",
                        `${url.pathname}${url.search}${url.hash}`
                      );
                      window.dispatchEvent(
                        new Event("open-share-profile-modal")
                      );
                    }}
                  >
                    <img src="/images/send-req.svg" alt=""></img>
                    {_t("shareApplicationWithLandlord", "Share with landlord")}
                  </p>
                  <p
                    className={`deleteText custom-gap-2${isProcessing ? " processing" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isProcessing) {
                        setOpenMenuId(null);
                        onDelete(app._id);
                      }
                    }}
                  >
                    {isProcessing ? (
                      <>
                        <img
                          src={SkorDefaultLoader}
                          className="delete-loader"
                          alt=""
                        ></img>
                        {_t("deleting", "Deleting...")}
                      </>
                    ) : (
                      <>
                        <img src="/images/delete-icon.svg" alt=""></img>
                        {_t("deleteApplication", "Delete application")}
                      </>
                    )}
                  </p>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {/* ── status badge + avatars ── */}
      <div
        className={`application-card-footer${activePendingSkorProtectAction && isSkorProtectPaymentPending ? " application-card-footer-with-payment" : ""}`}
      >
        {!llShowOnly && getStatusBadge()}
        {activePendingSkorProtectAction && isSkorProtectPaymentPending && (
          <button
            type="button"
            className="application-skor-protect-payment-button"
            disabled={isPaymentProcessing}
            onClick={async (event) => {
              event.stopPropagation();
              await onPaySkorProtect(activePendingSkorProtectAction);
            }}
          >
            {isPaymentProcessing ? (
              <>
                <span
                  className="application-skor-protect-payment-spinner"
                  aria-hidden="true"
                />
                {_t("redirecting")}
              </>
            ) : (
              <>
                {_t("paySkorProtect")}
                <ArrowForwardIcon />
              </>
            )}
          </button>
        )}
      </div>

      {/* ── expanded: tenants list ── */}
      {expanded && (
        <>
          {participants.length > 0 && (
            <div className="application-expanded-members">
              <p className="application-tenants-label">
                {_t("tenantsInThisApplication", "TENANTS IN THIS APPLICATION")}
              </p>
              {participants.map((p, i) => {
                const role = getRole(p);
                const guarantors = getParticipantGuarantors(p);
                const isDeleted = p.is_deleted === true;
                const isAccepted = p.consent_status === "accepted";
                const isMe =
                  !isDeleted && p.email?.toLowerCase() === currentUserEmail;
                const isOwnerRow = role === "owner";
                const hasTenantId = p.id && llShowOnly && !isDeleted;
                const canNavigate = hasTenantId && !isMe;

                /* display name: always show "You" when it's the current user */
                const displayName = isMe
                  ? _t("you", "You")
                  : resolveParticipantName(
                      p,
                      _t("deletedUser", "Deleted user")
                    );

                /* subtitle: "Owner" label for owner row, otherwise email */
                const subtitle = isDeleted
                  ? ""
                  : isOwnerRow
                    ? _t("owner", "Owner")
                    : (p.email ?? "");

                return (
                  <Fragment key={p.id ?? p.email ?? i}>
                    <div
                      className={`application-member-row${canNavigate ? " cursor-pointer" : ""}`}
                      style={isDeleted ? { opacity: 0.6 } : undefined}
                      onClick={() => {
                        if (canNavigate) {
                          const url = new URL(window.location.href);
                          url.searchParams.set("tenant_id", p.id);
                          window.history.pushState({}, "", url.toString());
                          window.dispatchEvent(new PopStateEvent("popstate"));
                        }
                      }}
                    >
                      {isDeleted ? (
                        <div className="member-avatar-chip member-avatar-lg member-avatar-chip-gray">
                          <img
                            src="/images/deleted-user-icon.svg"
                            alt={_t("deletedUser", "Deleted user")}
                            width={16}
                            height={16}
                          />
                        </div>
                      ) : (
                        <div
                          className={`member-avatar-chip member-avatar-lg member-avatar-chip-gray${isAccepted ? " member-avatar-chip-tenant" : ""}`}
                        >
                          {getInitials(isMe ? "Yo" : displayName)}
                        </div>
                      )}
                      <div className="application-member-info">
                        <p className="application-member-name">{displayName}</p>
                        <p className="application-member-email">{subtitle}</p>
                      </div>
                      <RoleBadge role={role} _t={_t} />
                      {canNavigate && (
                        <ChevronRightIcon
                          fontSize="small"
                          style={{ color: "#141B34", marginLeft: "8px" }}
                        />
                      )}
                    </div>
                    {guarantors.map((guarantor, guarantorIndex) => {
                      const guarantorName = resolveParticipantName(guarantor);
                      return (
                        <div
                          key={guarantor._id ?? guarantorIndex}
                          className="application-member-row application-guarantor-row"
                        >
                          <div className="member-avatar-chip member-avatar-lg member-avatar-chip-guarantor">
                            {getInitials(guarantorName)}
                          </div>
                          <div className="application-member-info">
                            <div className="application-guarantor-name-row">
                              <p className="application-member-name">
                                {guarantorName}
                              </p>
                              <span className="app-member-badge app-member-badge-guarantor">
                                {_t("guarantor", "Guarantor")}
                              </span>
                            </div>
                            {guarantor.email && (
                              <p
                                className="application-member-email"
                                title={guarantor.email}
                              >
                                {guarantor.email}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </Fragment>
                );
              })}
            </div>
          )}

          {/* ── expanded: shared with ── */}
          {totalShared > 0 && (
            <div className="application-expanded-members">
              <p className="application-tenants-label">
                {_t("sharedWithSection", "SHARED WITH")}
              </p>

              {shareRequests.map((sr, i) => {
                const landlord = sr.landlord_id ?? {};
                const sharedBy = sr.tenant_id ?? {};
                const landlordName =
                  `${landlord.fname_companyName ?? ""} ${landlord.lname ?? ""}`.trim() ||
                  landlord.email ||
                  "";
                const landlordEmail = landlord.email ?? "";
                const sharedByName =
                  `${sharedBy.fname ?? ""} ${sharedBy.lname ?? ""}`.trim() ||
                  sharedBy.email ||
                  "";
                return (
                  <div key={sr._id ?? i} className="application-member-row">
                    <div className="member-avatar-chip member-avatar-lg member-avatar-chip-gray">
                      {getInitials(landlordName || landlordEmail)}
                    </div>
                    <div className="application-member-info">
                      <p className="application-member-name">{landlordName}</p>
                      <p
                        className="application-member-email"
                        title={landlordEmail}
                      >
                        {landlordEmail}
                      </p>
                    </div>
                    <div className="app-shared-by-stack">
                      {sharedByName && (
                        <span className="app-shared-by-label">
                          {_t("sharedBy", "Shared by")} {sharedByName}
                        </span>
                      )}
                      <span className="app-member-badge app-member-badge-invited">
                        {_t("pending", "Pending")}
                      </span>
                    </div>
                  </div>
                );
              })}

              {sharedProfiles.map((sp, i) => {
                const landlord = sp.id?.landlordid ?? {};
                const sharedBy = sp.shared_by ?? {};
                const landlordName =
                  `${landlord.fname_companyName ?? ""} ${landlord.lname ?? ""}`.trim() ||
                  landlord.email ||
                  "";
                const landlordEmail = landlord.email ?? "";
                const sharedByName = sp.shared_by_deleted
                  ? _t("deletedUser", "Deleted user")
                  : `${sharedBy.fname ?? ""} ${sharedBy.lname ?? ""}`.trim() ||
                    sharedBy.email ||
                    "";
                return (
                  <div key={sp.id?._id ?? i} className="application-member-row">
                    <div className="member-avatar-chip member-avatar-lg member-avatar-chip-gray">
                      {getInitials(landlordName || landlordEmail)}
                    </div>
                    <div className="application-member-info">
                      <p className="application-member-name">{landlordName}</p>
                      <p
                        className="application-member-email"
                        title={landlordEmail}
                      >
                        {landlordEmail}
                      </p>
                    </div>
                    {sharedByName && (
                      <div className="app-shared-by-stack">
                        <span className="app-shared-by-label">
                          {_t("sharedBy", "Shared by")} {sharedByName}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ── main modal ── */
const ApplicationsModal = ({
  isOpen,
  onClose,
  applications,
  loading,
  onNewApplication,
  onApplicationsChange,
  llShowOnly,
  pendingSkorProtectActions = [],
}) => {
  const { _t } = useTranslator();
  const { user } = useContext(AuthContext);
  const currentUserEmail = user?.email?.toLowerCase();
  const currentUserId = user?._id;

  const {
    consentApplication,
    deleteApplication,
    createSkorProtectPaymentLink,
    loadPendingSkorProtectActions,
    getSkorProtectPaymentReceipt,
  } = useContext(ApplicationContext);
  const [processingId, setProcessingId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [paymentProcessingId, setPaymentProcessingId] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const resetPaymentProcessing = () => setPaymentProcessingId(null);

    window.addEventListener("pageshow", resetPaymentProcessing);
    return () => window.removeEventListener("pageshow", resetPaymentProcessing);
  }, []);

  useEffect(() => {
    if (!isOpen || pendingSkorProtectActions.length === 0) return undefined;

    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [isOpen, pendingSkorProtectActions.length]);

  const handleAccept = async (applicationId) => {
    try {
      setProcessingId(applicationId);
      await consentApplication(applicationId, "accepted");
    } catch (err) {
      toast.error(err?.response?.data?.message ?? _t("failedToAcceptRequest"));
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (applicationId) => {
    try {
      setProcessingId(applicationId);
      await consentApplication(applicationId, "rejected");
    } catch (err) {
      toast.error(err?.response?.data?.message ?? _t("failedToRejectRequest"));
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (applicationId) => {
    try {
      setProcessingId(applicationId);
      await deleteApplication(applicationId);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ?? _t("failedToDeleteApplication")
      );
    } finally {
      setProcessingId(null);
    }
  };

  const handlePaySkorProtect = async (pendingAction) => {
    try {
      setPaymentProcessingId(pendingAction.skor_protect_id);
      const paymentUrl = await createSkorProtectPaymentLink(
        pendingAction.skor_protect_id
      );
      if (!paymentUrl) throw new Error("Payment URL was not returned");
      window.location.assign(paymentUrl);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ??
          _t("failedToCreateSkorProtectPaymentLink")
      );
      loadPendingSkorProtectActions();
      setPaymentProcessingId(null);
    }
  };

  const handleViewPaymentReceipt = async (pendingAction) => {
    const receiptWindow = window.open("", "_blank");
    if (receiptWindow) receiptWindow.opener = null;

    try {
      const receiptUrl = await getSkorProtectPaymentReceipt(
        pendingAction.skor_protect_id
      );
      if (!receiptUrl) throw new Error("Receipt URL was not returned");

      if (receiptWindow) {
        receiptWindow.location.href = receiptUrl;
      } else {
        window.location.assign(receiptUrl);
      }
    } catch (err) {
      receiptWindow?.close();
      toast.error(
        err?.response?.data?.message ?? _t("failedToLoadPaymentReceipt")
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="wrapper-modal" onClick={onClose}>
      <div
        className={`doc-history-popup total-salary-modal${isOpen ? " open" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <span className="drawerIcon" onClick={onClose} />
        <div className="applications-modal-inner">
          <div className="applications-modal-header-block">
            <div
              className="doc-history-header applications-modal-header-row"
              style={{ paddingBottom: "0" }}
            >
              <img
                src={CloseIcon}
                alt=""
                className="doc-history-close-btn"
                onClick={onClose}
                style={{ flexShrink: 0 }}
              />
              <div
                className="d-flex gap-1 flex-column"
                style={{ flex: 1, minWidth: 0 }}
              >
                <h4 className="custom-modal-heading">
                  {_t("candidacies", "Candidacies")}
                </h4>
                <p className="applications-modal-subtitle">
                  {_t(
                    "applicationModelDescription",
                    "Preséntate al propietario junto a las personas con las que vas a vivir."
                  )}
                </p>
              </div>
              {!llShowOnly && (
                <button
                  className="addGuarantorButton d-flex align-items-center justify-content-center applications-modal-new-btn"
                  onClick={() => {
                    onClose();
                    onNewApplication();
                  }}
                  style={{ flexShrink: 0 }}
                >
                  <img src="/images/add-white.svg" alt="" />
                  {_t("newApplication")}
                </button>
              )}
            </div>
          </div>

          <div className="applications-list">
            {loading ? (
              <div className="applications-loader">
                <img
                  src={SkorDefaultLoader}
                  className="defaultSkorLoader"
                  alt="Loading"
                />
              </div>
            ) : (applications ?? []).length === 0 ? (
              <p className="applications-empty-text">
                {_t(
                  "noApplicationsYet",
                  "Aún no tienes candidaturas. Crea una e invita a tus compañeros de piso."
                )}
              </p>
            ) : (
              (applications ?? []).map((app) => (
                <ApplicationCard
                  key={app._id}
                  app={app}
                  currentUserEmail={currentUserEmail}
                  currentUserId={currentUserId}
                  onAccept={handleAccept}
                  onReject={handleReject}
                  onDelete={handleDelete}
                  processingId={processingId}
                  openMenuId={openMenuId}
                  setOpenMenuId={setOpenMenuId}
                  onCloseModal={onClose}
                  llShowOnly={llShowOnly}
                  pendingSkorProtectAction={pendingSkorProtectActions.find(
                    (action) =>
                      String(action.application_id) === String(app._id)
                  )}
                  now={now}
                  onPaySkorProtect={handlePaySkorProtect}
                  paymentProcessingId={paymentProcessingId}
                  onViewPaymentReceipt={handleViewPaymentReceipt}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationsModal;
